import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";

interface DeleteConfirmProps {
  isOpen: boolean;
  onClose: () => void;
  /** Exacte naam die de gebruiker moet overtypen om te bevestigen. */
  entityName: string;
  /** Label van het veld, bijv. "bedrijfsnaam". */
  label?: string;
  title?: string;
  /** Extra waarschuwingstekst onder de titel. */
  children?: React.ReactNode;
  onConfirm: () => Promise<void> | void;
}

/**
 * Gedeelde verwijdermodal: de gebruiker moet de exacte naam overtypen
 * voordat de verwijderknop actief wordt. Voorkomt per ongeluk verwijderen.
 */
export function DeleteConfirm({
  isOpen,
  onClose,
  entityName,
  label = "naam",
  title = "Verwijderen bevestigen",
  children,
  onConfirm,
}: DeleteConfirmProps) {
  const [typed, setTyped] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const matches = typed.trim().toLowerCase() === entityName.trim().toLowerCase();

  const close = () => {
    setTyped("");
    setError("");
    onClose();
  };

  const handleConfirm = async () => {
    if (!matches) return;
    try {
      setLoading(true);
      setError("");
      await onConfirm();
      close();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Verwijderen mislukt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={title}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={close} disabled={loading}>
            Annuleren
          </Button>
          <Button variant="danger" onClick={handleConfirm} disabled={!matches || loading}>
            Definitief verwijderen
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          <AlertTriangle size={30} className="shrink-0 mt-0.5" />
          <div>
            {children || (
              <span>
                Weet je zeker dat je <strong>{entityName}</strong> wilt verwijderen? Deze actie
                is onomkeerbaar.
              </span>
            )}
          </div>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Input
          label={`Typ de ${label} exact over om te bevestigen`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={entityName}
          autoFocus
        />
        <p className="text-xs text-slate-400">
          Typ precies: <span className="font-mono font-semibold text-slate-600">{entityName}</span>
        </p>
      </div>
    </Modal>
  );
}